import { tagLang } from "./betriebstag";
/* ══════════════════════════════════════════════════════════════════
   Was ein Fehler mitbringt, und wie er zu lesen ist (03.10.2026).

   Ohne Abhängigkeiten, damit `test/fehlerwerte.test.ts` es ohne Browser
   prüfen kann — `daten/klient.ts` zieht React und die Sprachdatei mit.

   **Die Werte gehören in den Satz.** Im Durchgang vom 03.10.2026 stand an
   vier Stellen der Platzhalter statt der Zahl: „für {sekunden} Sekunden
   gesperrt" (A-04, K-03), „Es fehlen Pflichtfelder ({felder})" (K-15),
   „{maschine} hat einen offenen Defekt … vom {datum}" (D-25). Die API hatte
   die Werte jedes Mal geschickt; sie kamen nur nicht bis in den Satz, weil
   jede Maske sie selbst hätte durchreichen müssen. Jetzt trägt der Fehler sie
   fertig mit (`Fehler.werte`), und `FehlerBand` liest sie selbst.
   ══════════════════════════════════════════════════════════════════ */

/** Schlüssel, deren Wert ein Tag ist — als `01.08.2026`, nicht als Code. */
const TAGESFELDER = /^(von|bis|gueltig_bis|datum|seit|erledigt_am)$/;

/**
 * Die Daten einer Fehlerantwort als Werte für den Satz.
 *
 * Nur Zeichenketten und Zahlen — ein Objekt oder eine Liste hat im Satz
 * keinen Platz und bliebe als `[object Object]` stehen. Ein ISO-Tag in einem
 * Tagesfeld wird zum deutschen Datum: „am 2026-08-01 abgelaufen" ist ein
 * Code, „am 01.08.2026" ein Satz.
 */
export function werteAusDaten(daten: Record<string, unknown> | null | undefined): Record<string, string | number> {
  const raus: Record<string, string | number> = {};
  if (!daten) return raus;
  for (const [k, v] of Object.entries(daten)) {
    if (typeof v !== "string" && typeof v !== "number") continue;
    // Ein Zeitpunkt zählt mit seinem deutschen Tag (N8-02).
    const tag = typeof v === "string" && TAGESFELDER.test(k) ? tagLang(v) : "";
    raus[k] = tag || (BETRAGSFELDER.test(k) ? betragDeutsch(v) : v);
  }
  return raus;
}

/**
 * Schlüssel, deren Wert ein Betrag ist — „232,00", nicht „232.00" (P9-06,
 * 05.10.2026: „Hier ist noch etwas abzurechnen: 232.00 € netto"). Die API
 * schickt Zahlen in ihrer Form; deutsch wird daraus erst hier.
 */
const BETRAGSFELDER = /^(netto|brutto|betrag|summe)$/;

/** Ein Betrag als deutsche Zahl mit zwei Stellen; was keine Zahl ist, bleibt, wie es kam. */
export function betragDeutsch(wert: string | number): string | number {
  const n = typeof wert === "number" ? wert : Number(String(wert).trim());
  if (!Number.isFinite(n) || (typeof wert === "string" && wert.trim() === "")) return wert;
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * **Ein Text, der zu lang ist** (V-30, V-26, Durchgang 03.10.2026). Die API
 * schickt bei jeder Längengrenze `{hoechstens, laenge}` mit
 * (`mitte/eingabe.ts`, Belegtexte, Bestellnummer) — in der Maske stand
 * trotzdem „Prüfe das Feld, das rot umrandet ist", ohne zu sagen, dass es zu
 * lang war und wie lang es sein darf. Ein `eingabe.*` mit beiden Zahlen hat
 * deshalb seinen eigenen Satz; ohne sie bleibt es beim allgemeinen.
 */
export function eingabeZuLang(schluessel: string, daten: Record<string, unknown> | null | undefined): boolean {
  return schluessel.startsWith("eingabe.")
    && typeof daten?.hoechstens === "number"
    && typeof daten?.laenge === "number";
}

/**
 * **Zu viele Einträge** (AG-33, Durchgang 03.10.2026): elf Empfänger, und die
 * Maske sagte nur „Prüfe das rot umrandete Feld". Die API schickt bei einer
 * Anzahlgrenze `{hoechstens, anzahl}` mit; dann gilt der eigene Satz.
 */
export function eingabeZuViele(schluessel: string, daten: Record<string, unknown> | null | undefined): boolean {
  return schluessel.startsWith("eingabe.")
    && typeof daten?.hoechstens === "number"
    && typeof daten?.anzahl === "number";
}

/**
 * **Eine Zahl außerhalb ihres Bereichs** (AG-07, AG-05, AG-06, Durchgang
 * 03.10.2026). −5 % Rabatt, Menge −3, Einzelpreis −10: Die API schickt die
 * Grenze mit (`{von, bis}` oder `{ab}`), in der Maske stand trotzdem „Prüfe
 * das Feld, das rot umrandet ist" — ohne den erlaubten Bereich. Nur Zahlen
 * zählen; ein Tag in `von`/`bis` ist ein Zeitraum und kein Bereich.
 * `kein_tarif` (AG-06): Die Zeile hat keinen Satz im Stamm, zu dem ein
 * geleertes Preisfeld zurückginge.
 */
export function eingabeGrenze(
  schluessel: string,
  daten: Record<string, unknown> | null | undefined,
): "fehler.eingabeBereich" | "fehler.eingabeAb" | "fehler.eingabeKeinTarif" | null {
  if (!schluessel.startsWith("eingabe.")) return null;
  if (daten?.kein_tarif === true) return "fehler.eingabeKeinTarif";
  if (typeof daten?.von === "number" && typeof daten?.bis === "number") return "fehler.eingabeBereich";
  if (typeof daten?.ab === "number") return "fehler.eingabeAb";
  return null;
}

/**
 * Eine Mailadresse, die keine ist (K-18): `mailOptional` in der API hängt
 * `adresse` oder `mehrere` an. Dann sagt der Satz, was falsch ist, statt
 * auf ein rot umrandetes Feld zu zeigen.
 */
export function eingabeMail(
  schluessel: string,
  daten: Record<string, unknown> | null | undefined,
): "eine" | "mehrere" | null {
  if (!schluessel.startsWith("eingabe.") || !/email$/.test(schluessel)) return null;
  if (daten?.mehrere === true) return "mehrere";
  if (typeof daten?.adresse === "string") return "eine";
  return null;
}

/**
 * Eine USt-IdNr., die keine ist (08.10.2026): `ustIdOptional` in der API hängt
 * `ust_id` und `grund` an — `land` (kein Länderkürzel vorn) oder `format`.
 */
export function eingabeUstId(
  schluessel: string,
  daten: Record<string, unknown> | null | undefined,
): "fehler.eingabeUstIdLand" | "fehler.eingabeUstIdFormat" | null {
  if (schluessel !== "eingabe.ust_id" || typeof daten?.ust_id !== "string") return null;
  return daten.grund === "format" ? "fehler.eingabeUstIdFormat" : "fehler.eingabeUstIdLand";
}

/**
 * Der Schlüssel für eine Antwort ohne Hülle — eine Seite, die nicht die API
 * geschrieben hat, sondern nginx davor.
 *
 * **`429` ist die Bremse des Tors** (T-16, 03.10.2026): Vor der Anmeldung
 * zählt nginx je Herkunft, und wenn es zuschlägt, antwortet es mit einer
 * HTML-Seite. Daraus wurde „Der Server hat geantwortet, aber nicht in einer
 * Form, die sich lesen lässt" — für jemanden, der nur zu schnell war. Seit
 * diesem Tag antwortet das Tor selbst in der Hülle
 * (`betrieb/nginx/machine-office.conf`); dieser Zweig fängt eine ältere
 * Fassung des Tors und jede andere Zwischenstation ab.
 */
export function schluesselOhneHuelle(status: number): string {
  if (status === 413) return "anfrage.zu_gross";
  if (status === 429) return "anfrage.zu_viele";
  return "antwort.unlesbar";
}

/**
 * Was die Antwort auf `POST /api/kontext` für die Sitzung heißt.
 *
 *   `da`    neues Token, weiter geht's
 *   `aus`   die Sitzung ist wirklich vorbei — **nur `401` und `403`**
 *   `netz`  keine Antwort, eine Zwischenstation (`502`, `503`), die Bremse:
 *           Darüber weiß niemand, ob die Sitzung noch gilt, und abmelden
 *           wäre geraten.
 *
 * Bis zum 03.10.2026 meldete jeder Fehlschlag ab (A-10, D-50, T-41): Ein
 * Funkloch über die Erneuerung alle zwei Minuten stellte Kalle mitten in der
 * Rücknahme auf die Anmeldemaske, und dort blieb er, auch als der Empfang
 * zurück war — obwohl das Cookie die ganze Zeit gültig war. `null` steht für
 * „keine Antwort".
 */
/**
 * Der Grund im Rumpf einer `401 sitzung.abgelaufen` (A-09) — `passwortwechsel`,
 * wenn die Verwaltung einen Wechsel „sofort" verlangt hat. Sonst `null`.
 */
export function endgrundAus(rumpf: unknown): string | null {
  if (!rumpf || typeof rumpf !== "object") return null;
  const daten = (rumpf as { daten?: unknown }).daten;
  if (!daten || typeof daten !== "object") return null;
  const grund = (daten as { grund?: unknown }).grund;
  return typeof grund === "string" && grund ? grund : null;
}

export function kontextAusgang(status: number | null): "da" | "aus" | "netz" {
  if (status === null) return "netz";
  if (status >= 200 && status < 300) return "da";
  if (status === 401 || status === 403) return "aus";
  return "netz";
}
